import React, { useState, useRef } from 'react';
import './ContactForm.css';
import axiosInstance from '../../../services/AxiosInstance';
import ReCaptchaComponent from '../ReCaptcha';
import '../../../css/recaptcha.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // reCAPTCHA states
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  // reCAPTCHA configuration - Replace with your actual site key
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear reCAPTCHA error when user starts typing
    if (recaptchaError) {
      setRecaptchaError("");
    }
  };

  const handleRecaptchaExpire = () => {
    console.log('reCAPTCHA v3 token expired for contact form');
    setRecaptchaToken(null);
    setRecaptchaError("reCAPTCHA expired. Please try again.");
  };

  const handleRecaptchaError = (error) => {
    console.error('reCAPTCHA v3 error for contact form:', error);
    setRecaptchaError("reCAPTCHA verification failed. Please try again.");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setShowSuccess(false);
    setRecaptchaError("");
    
    // Execute reCAPTCHA on form submit
    if (recaptchaRef.current) {
      recaptchaRef.current.execute();
    }
  };

  const handleRecaptchaVerify = (token) => {
    console.log('reCAPTCHA v3 token received for contact form:', token);
    setRecaptchaToken(token);
    setRecaptchaError("");
    
    // Now submit the form with the token
    submitFormWithToken(token);
  };

  const submitFormWithToken = (token) => {
    // Prepare payload with reCAPTCHA data
    const payload = {
      ...formData,
      recaptchaToken: token,
      recaptchaAction: 'contact_form'
    };
    
    axiosInstance.post('/modules/contactus', payload)
      .then(response => {
        setIsSubmitting(false);
        setShowSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setRecaptchaToken(null);
        
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      })
      .catch(error => {
        setIsSubmitting(false);
        setRecaptchaToken(null);
        console.error('Error submitting contact form:', error);
        
        // Reset reCAPTCHA on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      });
  };

  return (
    <div className="contact-form-container">
      <div className="form-header">
        <h2>Message us directly</h2>
        <p>Fill out the form below and we'll get back to you within 24 hours.</p>
      </div>
      
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter your full name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email address"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="subject">Subject</label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder="What's this about?"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="message">Message *</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows="6"
            placeholder="Tell us more about your inquiry..."
          ></textarea>
        </div>
        
        {showSuccess && (
          <div className="success-message">
            <span className="success-text"> Message sent successfully, we will get back to you soon</span>
          </div>
        )}
        
        {/* reCAPTCHA Error Display */}
        {recaptchaError && (
          <div className="recaptcha-error">
            {recaptchaError}
          </div>
        )}
        
        {/* reCAPTCHA v3 Component */}
        <ReCaptchaComponent
          ref={recaptchaRef}
          siteKey={RECAPTCHA_SITE_KEY}
          version="v3"
          action="contact_form"
          onVerify={handleRecaptchaVerify}
          onExpire={handleRecaptchaExpire}
          onError={handleRecaptchaError}
          autoExecute={false}
        />
        
        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm; 